import React from 'react';
import { Progress, Button } from 'reactstrap';
import moment from 'moment';
import { faDownload, faFileExcel, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    LinkCellRenderer,
    TextFilterDefaults,
    indexCellRenderer,
    EmployeeAccountStatusRenderer,
    getMappedFilterModel,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import {
    FlexEndRow, PageHeading, PageWrap, SmallButton,
    toasty,
    FormBlocker,
} from '../common/forms/FormElements';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import authService from '../api-authorization/AuthorizeService';
import { Can } from '../Can';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { Modal } from '../common/Modal';
import { PayrollAppTypes } from '../tenant/Tenant';
import { EmployeeMap } from './EmployeeMap';
import EmployeeImportSlideout from './EmployeeImportSlideout';

const onApplyDispatchSelection = async (values) => {
    const { selectedMultiDispatchLocations } = values;

    if (!selectedMultiDispatchLocations) {
        toasty.error('Please select dispatch location(s) and week');
        return;
    }

    const model = selectedMultiDispatchLocations.map((e) => e.value);
    const fileName = `Employee_Certification_Report_${moment().format('YYYYMMDD_hhmmss')}.xlsx`;

    try {
        await httpClient.downloadFile(
            ApiRoutes.employees.certificationReportExport(),
            model,
            fileName,
        );
        toasty.success('Certification report downloaded successfully');
    } catch (error) {
        toasty.error('Error downloading certification report');
        console.error(error);
    }
};

const getInitialListFiltersFromCache = (initialFilters) => {
    const json = sessionStorage.getItem('employee_gridFilterSettings');

    if (json) {
        const obj = JSON.parse(json);

        initialFilters.groupId = obj.Group ? parseInt(obj.Group.value) : 0;
        initialFilters.state = obj['EmployeeLocations.EmployeeLocation.State'] ? obj['EmployeeLocations.EmployeeLocation.State'].value : '';
        initialFilters.dispatchId = obj['DispatchCompany.CompanyName'] ? parseInt(obj['DispatchCompany.CompanyName'].value) : 0;
    }

    return initialFilters;
};

const getOfficeCoords = (cmp) => {
    const latitude = Number(cmp?.latitude);
    const longitude = Number(cmp?.longitude);
    return Number.isFinite(latitude) && Number.isFinite(longitude)
        ? { lat: latitude, lng: longitude }
        : null;
};

class EmployeeIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            selectedRow: null,
            isMapOpen: false,
            mapCenter: null,
            employees: [],
            dispatchLocations: [],
            homeOfficeCenter: null,
            showDispatchModal: false,
            selectedMultiDispatchLocations: null,
            isExporting: false,
            isImporting: false,
            showImportSlideout: false,
            selectedMapEmployeeId: null,
            mapWarningMessage: null,
        };
        this.importSlideoutRef = React.createRef();
        this.onAddEmployeeClicked = this.onAddEmployeeClicked.bind(this);
        this.onOnboardEmployeeClicked = this.onOnboardEmployeeClicked.bind(this);
        this.toggleMapModal = this.toggleMapModal.bind(this);
        this.toggleExportModal = this.toggleExportModal.bind(this);
        this.toggleImportSlideout = this.toggleImportSlideout.bind(this);
        this.onImportSuccess = this.onImportSuccess.bind(this);
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    async loadEmployeesforMap() {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const employeeSearchDTO = this.getEmployeeSearchDTO();
            const employees = await httpClient.post(ApiRoutes.employees.fetchEmployeesForMap(), employeeSearchDTO);
            this.setState({ employees });
        }
    }
    getEmployeeSearchDTO = () => {
        const { gridOptions } = this.state;
        if (!gridOptions?.api) return;
        const filterModelRaw = gridOptions.api.getFilterModel() || {};
        const sortModel = gridOptions.api.getSortModel?.() || [];
        const filterModelMapped = getMappedFilterModel(
            { ...filterModelRaw },
            { ...gridOptions },
        );
        return {
            startRow: 0,
            endRow: 200,
            FilterModel: filterModelMapped,
            sort: sortModel,
        };
    };
    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    onAddEmployeeClicked = () => this.context.history.push(`${AppNavPaths.EmployeeNew}`);

    onOnboardEmployeeClicked = () => this.context.history.push(AppNavPaths.EmployeeNewHire);

    onCenterChanged = (center) => {
        this.setState({ mapCenter: center });
    };

    onGridFilterChanged = () => {
        const { gridApi } = this.state;
        if (!gridApi) return;
        const filterModel = gridApi.getFilterModel();
        let dispatchFilter = filterModel['DispatchCompany.CompanyName']?.value || null;
        const dispatchLocations = this.state.dispatchLocations || [];
        if (!dispatchFilter) {
            const { user } = { ...this.context };
            dispatchFilter = user?.dispatchCompanyId;
        }
        if (!dispatchFilter) return;
        let match = dispatchLocations.find((cmp) => cmp.value === dispatchFilter);
        if (!match) {
            match = dispatchLocations?.[0];
        }
        if (match) {
            const pos = getOfficeCoords(match);
            if (pos) {
                this.setState({ mapCenter: pos, homeOfficeCenter: pos });
            }
        }
    };

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            const [groups, accountStatuses, usStates, dispatchLocations, payrollCompanies] = await Promise.all([
                httpClient.js(ApiRoutes.auth.groups()),
                httpClient.js(ApiRoutes.employeeAccountStatuses.all()),
                httpClient.js(ApiRoutes.typeAheads.USStates()),
                httpClient.js(ApiRoutes.typeAheads.dispatchLocations()),
                httpClient.js(ApiRoutes.typeAheads.payrollCompanies()),
            ]);

            accountStatuses.unshift({
                id: -1,
                description: 'Active & New Hire',
            });

            const gridOptions = createGridOptions(this);
            const context = this;

            let initialListFilters = {
                groupId: 0,
                state: '',
                dispatchId: 0,
            };
            initialListFilters = getInitialListFiltersFromCache(initialListFilters);

            const quirksEnabled = this.context.tenant?.tenantSettings?.quirksEnabled;
            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                nameRenderer: LinkCellRenderer,
                employeeAccountStatusRenderer: EmployeeAccountStatusRenderer,
                indexCellRenderer,
            };

            const groupFilterParams = {
                suppressFilterButton: true,
                options: groups,
                optionsLabel: 'groupName',
                optionsValue: 'id',
                initialFilterValue: initialListFilters.groupId,
            };
            const statusFilterParams = {
                suppressFilterButton: true,
                options: accountStatuses,
                optionsLabel: 'description',
                optionsValue: 'id',
                initialFilterValue: -1,
            };
            const stateFilterParams = {
                suppressFilterButton: true,
                options: usStates,
                optionsLabel: 'label',
                optionsValue: 'value',
                initialFilterValue: initialListFilters.state,
            };
            const dispatchFilterParams = {
                suppressFilterButton: true,
                labelText: 'Filter by Dispatch',
                options: dispatchLocations,
                optionsLabel: 'label',
                optionsValue: 'value',
                initialFilterValue: initialListFilters.dispatchId,
            };

            gridOptions.columnDefs = [
                {
                    flex: 0,
                    width: 70,
                    minWidth: 70,
                    maxWidth: 70,
                    headerName: '',
                    valueGetter: 'node.id',
                    sortable: false,
                    cellRenderer: 'indexCellRenderer',
                },
                {
                    colId: 'EmployeeUser.User.UserName',
                    sortable: true,
                    headerName: 'Username',
                    field: 'userName',
                    tooltipField: 'userName',
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: 'nameRenderer',
                    cellRendererParams: {
                        to: (id) => `${AppNavPaths.Employee}/${id}`,
                        nameField: 'userName',
                        idField: 'id',
                        title: 'View this Employee',
                    },
                },
                {
                    colId: 'LastName',
                    sortable: true,
                    headerName: 'Last Name',
                    field: 'lastName',
                    tooltipField: 'lastName',
                    sort: { direction: 'asc', priority: 0 },
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: 'employeeAccountStatusRenderer',
                    cellRendererParams: {
                        nameField: 'lastName',
                    },
                },
                {
                    colId: 'FirstName',
                    sortable: true,
                    headerName: 'First Name',
                    field: 'firstName',
                    tooltipField: 'firstName',
                    sort: { direction: 'asc' },
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: 'employeeAccountStatusRenderer',
                    cellRendererParams: {
                        nameField: 'firstName',
                    },
                },
                {
                    colId: 'Group',
                    sortable: false,
                    headerName: 'Groups',
                    field: 'groupsFlat',
                    tooltipField: 'groupsFlat',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: groupFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: groupFilterParams,
                },
                {
                    colId: 'EmployeeLocations.EmployeeLocation.City',
                    sortable: false,
                    sort: { direction: 'asc' },
                    headerName: 'City',
                    field: 'homeAddress.city',
                    tooltipField: 'homeAddress.city',
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'EmployeeLocations.EmployeeLocation.State',
                    sortable: false,
                    sort: { direction: 'asc' },
                    headerName: 'State',
                    field: 'homeAddress.state',
                    tooltipField: 'homeAddress.state',
                    flex: 0,
                    width: 140,
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: stateFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: stateFilterParams,
                },
                {
                    colId: 'DispatchCompany.CompanyName',
                    headerName: 'Dispatching',
                    sortable: true,
                    field: 'dispatchOfficeLocation',
                    tooltipField: 'dispatchOfficeLocation',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: dispatchFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: dispatchFilterParams,
                },
                {
                    colId: 'Email',
                    sortable: true,
                    field: 'email',
                    tooltipField: 'email',
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'MobilePhone',
                    sortable: false,
                    field: 'mobilePhone',
                    tooltipField: 'mobilePhone',
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'AccountStatus.Description',
                    sortable: true,
                    field: 'accountStatus',
                    tooltipField: 'accountStatus',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: statusFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: statusFilterParams,
                },
                {
                    colId: 'EmployeeNumber',
                    sortable: true,
                    hide: quirksEnabled,
                    field: 'employeeNumber',
                    tooltipField: 'employeeNumber',
                    filter: 'agTextColumnFilter',
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
            ];

            /// ///////////////////////////////////////////////////////
            // RJP - Cache and reload filter and sort changes
            gridOptions.onFilterChanged = () => {
                const filterModel = gridOptions.api.getFilterModel();
                sessionStorage.setItem('employee_gridFilterSettings', JSON.stringify(filterModel));
                if (this.state.isMapOpen) {
                    this.loadEmployeesforMap();
                    this.onGridFilterChanged();
                }
            };

            gridOptions.onSortChanged = () => {
                const sortModel = gridOptions.api.columnModel.getColumnState();
                sessionStorage.setItem('employee_gridSortSettings', JSON.stringify(sortModel));
            };

            gridOptions.onRowClicked = (params) => {
                if (!this.state.isMapOpen) return;
                const employeeId = params.data?.id;
                const lat = params.data?.homeAddress?.latitude;
                const lng = params.data?.homeAddress?.longitude;
                if (employeeId && lat != null && lng != null) {
                    this.setState({
                        selectedMapEmployeeId: employeeId,
                        mapCenter: { lat, lng },
                        mapWarningMessage: null,
                    });
                } else if (employeeId) {
                    const name = `${params.data?.firstName || ''} ${params.data?.lastName || ''}`.trim();
                    this.setState({
                        selectedMapEmployeeId: null,
                        mapWarningMessage: `No location data for ${name || 'this employee'}`,
                    });
                }
            };

            gridOptions.onFirstDataRendered = () => {
                const sortModel = sessionStorage.getItem('employee_gridSortSettings');

                if (sortModel && gridOptions.api.setSortModel) {
                    const obj = JSON.parse(sortModel);
                    gridOptions.api.setSortModel(obj);
                }
            };

            gridOptions.onGridReady = (params) => {
                const filterModel = sessionStorage.getItem('employee_gridFilterSettings');
                if (filterModel) {
                    const obj = JSON.parse(filterModel);

                    // Have to set individually here instead of using the whole model
                    // due to list types requiring a different initialization routine
                    if (obj['EmployeeUser.User.UserName']) {
                        const filterInstance = gridOptions.api.getFilterInstance('EmployeeUser.User.UserName');
                        filterInstance.setModel(obj['EmployeeUser.User.UserName']);
                        gridOptions.api.onFilterChanged();
                    }
                    if (obj.LastName) {
                        const filterInstance = gridOptions.api.getFilterInstance('LastName');
                        filterInstance.setModel(obj.LastName);
                        gridOptions.api.onFilterChanged();
                    }
                    if (obj.FirstName) {
                        const filterInstance = gridOptions.api.getFilterInstance('FirstName');
                        filterInstance.setModel(obj.FirstName);
                        gridOptions.api.onFilterChanged();
                    }
                    if (obj['EmployeeLocations.EmployeeLocation.City']) {
                        const filterInstance = gridOptions.api.getFilterInstance('EmployeeLocations.EmployeeLocation.City');
                        filterInstance.setModel(obj['EmployeeLocations.EmployeeLocation.City']);
                        gridOptions.api.onFilterChanged();
                    }
                    if (obj.Email) {
                        const filterInstance = gridOptions.api.getFilterInstance('Email');
                        filterInstance.setModel(obj.Email);
                        gridOptions.api.onFilterChanged();
                    }
                    if (obj.MobilePhone) {
                        const filterInstance = gridOptions.api.getFilterInstance('MobilePhone');
                        filterInstance.setModel(obj.MobilePhone);
                        gridOptions.api.onFilterChanged();
                    }
                }

                params.api.setDatasource(context.state.dataSource);
                context.setState({ gridApi: params.api });
            };
            // END - Cache and reload filter and sort changes
            /// //////////////////////////////////////////////////////

            const ds = createDataSource(ApiRoutes.employees.search(), gridOptions);

            this.setState({
                loading: false,
                gridOptions,
                dataSource: ds,
                dispatchLocations,
                payrollCompanies,
            });
        }
    }

    toggleMapModal = () => {
        this.setState((prev) => {
            const newMapOpen = !prev.isMapOpen;
            if (newMapOpen) {
                this.loadEmployeesforMap();
                this.onGridFilterChanged();
            }
            return { isMapOpen: newMapOpen, selectedMapEmployeeId: null, mapWarningMessage: null };
        });
    };

    toggleExportModal = () => {
        Modal.show({
            title: 'Export Certification Report',
            onSubmit: (values) => onApplyDispatchSelection(values),
            SaveButtonProps: {
                label: 'Export',
                savingLabel: 'Exporting',
                icon: faDownload,
            },
            body: ({ setFieldValue }) => (
                <ValidatedSelect
                    placeholder="Select Dispatching"
                    label="Dispatching"
                    isClearable
                    isMulti
                    id="selectedMultiDispatchLocations"
                    name="selectedMultiDispatchLocations"
                    options={this.state.dispatchLocations}
                    onSelectAll={() => {
                        setFieldValue('selectedMultiDispatchLocations', this.state.dispatchLocations);
                    }}
                    required
                    error="A dispatch office must be selected"
                />
            ),
        });
    };

    toggleImportSlideout(show = null) {
        this.setState((prev) => ({
            showImportSlideout: show !== null ? show : !prev.showImportSlideout,
        }));
    }

    onImportSuccess = () => {
        if (this.state.gridApi) {
            this.state.gridApi.refreshInfiniteCache();
        }
    };

    importADPEmployees = () => {
        const { payrollCompanies } = this.state;
        const companies = payrollCompanies.filter((c) => c.payrollExternalId);

        if (companies.length === 0) {
            toasty.error('No payroll companies configured for ADP import');
        } else if (companies.length == 1){
            this.importADPEmployeesByCompany({ payrollCompany: companies[0].value });
        } else {
            Modal.show({
                title: 'Import ADP Employees',
                onSubmit: (values) => this.importADPEmployeesByCompany(values),
                SaveButtonProps: {
                    label: 'Import',
                },
                body: () => (
                    <ValidatedSelect
                        id="payrollCompany"
                        name="payrollCompany"
                        label="Payroll Company"
                        options={companies}
                        required
                        error="You must select a payroll company"
                    />
                ),
            });
        }
    };

    importADPEmployeesByCompany = async (values) => {
        this.setState({ isImporting: true });
        try {
            const response = await httpClient.get(ApiRoutes.employeeImport.all(values.payrollCompany));
            const externalEmployees = response?.data || [];
            toasty.success('Employees imported successfully');
            if (externalEmployees.length > 0) {
                this.setState({ showImportSlideout: true }, () => {
                    if (this.importSlideoutRef.current) {
                        this.importSlideoutRef.current.open(externalEmployees);
                    }
                });
            }
            this.onImportSuccess();
        } catch (error) {
            const errorMessage = error?.handledError?.message
                || 'Error importing employees';
            toasty.error(errorMessage);
        } finally {
            this.setState({ isImporting: false });
        }
    };

    render() {
        if (this.state.loading || !this.context?.permissions || !this.context.tenant.tenantSettings) return (<Progress />);

        const { tenant } = { ...this.context };
        const { employeeOnboardingEnabled, googleMapsApiKey, payrollAppTypeId } = { ...tenant.tenantSettings };
        const {
            rowData, gridOptions, isMapOpen, isImporting, showImportSlideout,
        } = { ...this.state };

        return (
            <PageWrap id="employeeIndexPage">
                <FormBlocker
                    show={isImporting}
                    showProgress
                />
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faUser}
                        className="mr-2 text-muted"
                    />
                    <span>Employees</span>
                    <span
                        style={{
                            float: 'right',
                            position: 'relative',
                            top: '-5px',
                        }}
                    >
                        {payrollAppTypeId === PayrollAppTypes.ADP && (
                            <Can
                                I="onboard"
                                a="employee"
                            >
                                <Button
                                    size="sm"
                                    color="warning"
                                    style={{ marginRight: '10px' }}
                                    onClick={() => this.importADPEmployees()}
                                    disabled={isImporting}
                                >
                                    {isImporting ? 'Importing...' : 'Import ADP Employees'}
                                </Button>
                                <Button
                                    size="sm"
                                    color="success"
                                    style={{ marginRight: '10px' }}
                                    onClick={() => {
                                        this.setState({ showImportSlideout: true }, () => {
                                            if (this.importSlideoutRef.current) {
                                                this.importSlideoutRef.current.open([]);
                                            }
                                        });
                                    }}
                                >
                                    Process External Employees
                                </Button>
                            </Can>
                        )}
                        <Button
                            size="sm"
                            color="success"
                            onClick={() => this.toggleMapModal()}
                        >
                            {this.state.isMapOpen ? 'Close Map' : 'Open Map'}
                        </Button>
                    </span>
                </PageHeading>
                <div className="d-flex flex-row flex-nowrap flex-fill">
                    <div className="map-left">
                        <DataGridToolbar
                            entity="employee"
                            gridApi={this.state.gridApi}
                            dataSource={this.state.dataSource}
                            hideAdd
                            addIcon="fa-user-plus"
                            hideExcelButton
                            gridOptions={this.state.gridOptions}
                            serverExport={{ apiPath: ApiRoutes.employees.excelExport(), filePrefix: 'EmployeeSearch' }}
                            serverExportDisabled={!!this.state.saving || !!this.state.loading || !!this.state.loadingData}
                        >
                            <SmallButton
                                disabled={null}
                                onClick={() => this.toggleExportModal()}
                            >
                                <FontAwesomeIcon
                                    icon={faFileExcel}
                                    size="lg"
                                />
                                <span className="ml-2 small-viewport-hide">
                                    Certification Report
                                </span>
                            </SmallButton>
                            <Can
                                I="create"
                                a="employee"
                            >
                                <FlexEndRow>
                                    <Can
                                        I="add"
                                        a="employee"
                                    >
                                        <SmallButton
                                            type="button"
                                            onClick={this.onAddEmployeeClicked}
                                        >
                                            <i className="fa fa-md mr-2 fa-user-plus" />
                                            Add
                                        </SmallButton>
                                    </Can>
                                    {
                                        !!employeeOnboardingEnabled
                                        && (
                                            <Can
                                                I="onboard"
                                                a="employee"
                                            >
                                                <SmallButton
                                                    type="button"
                                                    onClick={this.onOnboardEmployeeClicked}
                                                >
                                                    <i className="fa fa-md mr-2 fa-user-plus" />
                                                    New Hire
                                                </SmallButton>
                                            </Can>
                                        )
                                    }
                                </FlexEndRow>
                            </Can>
                        </DataGridToolbar>
                        <DataGrid
                            domLayout="normal"
                            rowData={rowData}
                            gridOptions={gridOptions}
                            gridStatus={this.state.gridStatus}
                        />
                    </div>
                    {isMapOpen && (
                        <div
                            className={`map-right${isMapOpen ? ' show' : ''
                                }`}
                        >
                            <EmployeeMap
                                employees={this.state.employees?.rows}
                                center={this.state.mapCenter}
                                apiKey={googleMapsApiKey}
                                mapId={this.context.tenant?.tenantSettings?.mapId}
                                onCenterChange={this.onCenterChanged}
                                baseCenter={this.state.homeOfficeCenter}
                                selectedEmployeeId={this.state.selectedMapEmployeeId}
                                warningMessage={this.state.mapWarningMessage}
                                onWarningClose={() => this.setState({ mapWarningMessage: null })}
                            />
                        </div>
                    )}
                </div>
                <EmployeeImportSlideout
                    show={showImportSlideout}
                    toggleShow={this.toggleImportSlideout}
                    onClose={() => this.toggleImportSlideout(false)}
                    onImportSuccess={this.onImportSuccess}
                    ref={this.importSlideoutRef}
                />
            </PageWrap>

        );
    }
}

export default EmployeeIndex;
